import { FC } from "react"
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  DeleteOutline,
} from "@mui/icons-material"
import { UserbotCatalog } from "../../interfaces"

const FIELD_TYPES: UserbotCatalog.FieldType[] = [
  "string",
  "text",
  "number",
  "boolean",
  "chatId",
  "clientId",
  "role",
  "entity",
  "llmTask",
  "matcherType",
  "cron",
  "enum",
  "json",
  "array",
  "object",
  "oneOf",
]

const OPTIONS_SOURCES: UserbotCatalog.OptionsSource[] = [
  "clients",
  "roles",
  "entities",
  "llmTasks",
  "matcherTypes",
]

const SELECT_TYPES: UserbotCatalog.FieldType[] = [
  "clientId",
  "role",
  "entity",
  "llmTask",
  "matcherType",
]

interface IProps {
  variables: UserbotCatalog.FieldSpec[]
  onChange: (variables: UserbotCatalog.FieldSpec[]) => void
}

const emptyVariable = (): UserbotCatalog.FieldSpec => ({
  key: "",
  label: "",
  type: "string",
  required: false,
})

export const VariablesEditor: FC<IProps> = ({ variables, onChange }) => {
  const patch = (index: number, over: Partial<UserbotCatalog.FieldSpec>) =>
    onChange(variables.map((v, i) => (i === index ? { ...v, ...over } : v)))

  const remove = (index: number) =>
    onChange(variables.filter((_, i) => i !== index))

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir
    if (target < 0 || target >= variables.length) return
    const next = [...variables]
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }

  return (
    <Stack spacing={2}>
      {variables.map((v, i) => (
        <Paper key={i} variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Ключ (key)"
                value={v.key}
                onChange={(e) => patch(i, { key: e.target.value })}
                required
                size="small"
                sx={{ flex: 1 }}
              />
              <TextField
                label="Подпись (label)"
                value={v.label}
                onChange={(e) => patch(i, { label: e.target.value })}
                required
                size="small"
                sx={{ flex: 1 }}
              />
              <TextField
                select
                label="Тип"
                value={v.type}
                onChange={(e) =>
                  patch(i, {
                    type: e.target.value as UserbotCatalog.FieldType,
                  })
                }
                size="small"
                sx={{ minWidth: 130 }}
              >
                {FIELD_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
              <FormControlLabel
                control={
                  <Switch
                    checked={v.required}
                    onChange={(e) => patch(i, { required: e.target.checked })}
                  />
                }
                label="Обяз."
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!v.templatable}
                    onChange={(e) =>
                      patch(i, { templatable: e.target.checked || undefined })
                    }
                  />
                }
                label="{{}}"
              />
              <Box>
                <Tooltip title="Вверх">
                  <span>
                    <IconButton
                      size="small"
                      disabled={i === 0}
                      onClick={() => move(i, -1)}
                    >
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Вниз">
                  <span>
                    <IconButton
                      size="small"
                      disabled={i === variables.length - 1}
                      onClick={() => move(i, 1)}
                    >
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Удалить">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => remove(i)}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField
                label="Значение по умолчанию"
                value={v.default != null ? String(v.default) : ""}
                onChange={(e) =>
                  patch(i, {
                    default: e.target.value === "" ? undefined : e.target.value,
                  })
                }
                size="small"
                sx={{ flex: 1 }}
              />
              <TextField
                label="Подсказка (help)"
                value={v.help ?? ""}
                onChange={(e) =>
                  patch(i, { help: e.target.value || undefined })
                }
                size="small"
                sx={{ flex: 2 }}
              />
            </Stack>

            {v.type === "enum" && (
              <TextField
                label="Значения enum (через запятую)"
                value={(v.enumValues ?? []).join(", ")}
                onChange={(e) =>
                  patch(i, {
                    enumValues: e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
                size="small"
                fullWidth
              />
            )}

            {SELECT_TYPES.includes(v.type) && (
              <TextField
                select
                label="Источник опций (optionsSource)"
                value={v.optionsSource ?? ""}
                onChange={(e) =>
                  patch(i, {
                    optionsSource:
                      (e.target.value as UserbotCatalog.OptionsSource) ||
                      undefined,
                  })
                }
                size="small"
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="">—</MenuItem>
                {OPTIONS_SOURCES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {v.type === "array" && (
              <TextField
                select
                label="Тип элемента массива"
                value={v.item?.type ?? "string"}
                onChange={(e) =>
                  patch(i, {
                    item: {
                      key: "item",
                      label: "Элемент",
                      required: false,
                      type: e.target.value as UserbotCatalog.FieldType,
                    },
                  })
                }
                size="small"
                sx={{ minWidth: 200 }}
              >
                {FIELD_TYPES.filter((t) => t !== "array").map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>
        </Paper>
      ))}

      <Box>
        <Button
          startIcon={<Add />}
          onClick={() => onChange([...variables, emptyVariable()])}
        >
          Добавить переменную
        </Button>
      </Box>

      {variables.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Переменных нет.
        </Typography>
      )}
    </Stack>
  )
}
